import { action, computed, makeAutoObservable, observable } from 'mobx';
import request from '@stores/decorators/request';
import InterviewPracticeRepo, {
  RemoteInterviewPracticeRepo,
} from '@repository/interviewPractices/InterviewPracticesRepo';
import TtsRepo, { RemoteTtsRepo } from '@repository/tts/TtsRepo';
import AccTimer, { ACCTimerOption } from './AccTimer';
import { InterviewPracticeQuestionPolicyRs } from '@domain/rs/pass/interview/practice/InterviewPracticeQuestionPolicyRs';
import {
  QuestionAnswerResultRs,
  QuestionHelpContentTypeCode,
} from '@domain/rs/pass/interview/practice/QuestionAnswerResultRs';
import { PracticeQuestionTextRs } from '@domain/rs/pass/interview/practice/PracticeQuestionTextRs';
import { DEFAULT_CONSTRAINTS, DEFAULT_OPTIONS, Recorder, WebcamStatus } from '@domain/constant/webcamTypes';
import WebcamUtil from '@utils/WebcamUtil';

const MAX_TEST_TIME = 5;

class TtsService {
  private readonly repo: TtsRepo;

  constructor() {
    this.repo = new RemoteTtsRepo();
    makeAutoObservable(this, {}, { autoBind: true });
  }

  @request
  @action
  async getTts(text: string) {
    const res = await this.repo.getTts(text);
    if (res.status === 200) {
      const data = res.data as unknown as Blob | MediaSource;
      return new Audio(URL.createObjectURL(data));
    }
    return new Audio();
  }
}

// TODO 추후 스토어나 적절한 Layer로 분리해야합니다.
// 1 store에 다 넣는 건 좋지 않을 것 같아서 일단 class로 분리 해둔 것이기 때문입니다.
class RecorderService {
  @observable recorder: Recorder | null = null;
  @observable lastRecordingData: Blob | null = null;
  @observable status: WebcamStatus = WebcamStatus.UNINITIALIZED;
  @observable private _recordingOrWebcamOrMicError = false;
  @observable reconnected = false;
  private _cameraPerm: PermissionStatus | null = null;
  private _micPerm: PermissionStatus | null = null;

  constructor(private readonly pauseTtsAudio: () => void) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  readonly setRecordingOrWebcamOrMicError = (value: boolean) => {
    if (value) {
      this.pauseTtsAudio();
    }
    this.recordingOrWebcamOrMicError = value;
  };

  @action.bound
  async initialize(options = DEFAULT_OPTIONS, streamConstraints: MediaStreamConstraints = DEFAULT_CONSTRAINTS) {
    try {
      this.recorder = await WebcamUtil.getRecorderPromise(options, streamConstraints);
      this.status = WebcamStatus.INACTIVE;

      // From ACC
      // 아래 코드는 마이크나 카메라 끊김을 체크하기 위함입니다
      const setRecordingOrWebcamOrMicError = this.setRecordingOrWebcamOrMicError;
      this._cameraPerm = await navigator.permissions.query({ name: 'camera' } as unknown as PermissionDescriptor);
      this._cameraPerm.onchange = (event) => {
        if ((event.currentTarget as PermissionStatus).state === 'denied') {
          setRecordingOrWebcamOrMicError(true);
        }
      };

      this._micPerm = await navigator.permissions.query({ name: 'microphone' } as unknown as PermissionDescriptor);
      this._micPerm.onchange = (event) => {
        if ((event.currentTarget as PermissionStatus).state === 'denied') {
          setRecordingOrWebcamOrMicError(true);
        }
      };
      if (this.recorder.stream.getVideoTracks().length > 0) {
        this.recorder.stream.getVideoTracks()[0].onended = () => {
          //it can be fired when audio permission denied
          setRecordingOrWebcamOrMicError(true);
        };
      }
      if (this.recorder.stream.getAudioTracks().length > 0) {
        this.recorder.stream.getAudioTracks()[0].onended = () => {
          setRecordingOrWebcamOrMicError(true);
        };
      }
    } catch (e: any) {
      try {
        if (!this.reconnected) {
          this.reconnected = true;
          await this.reconnect();
        }
      } catch (e: any) {
        console.error(e);
        this.recordingOrWebcamOrMicError = true;
      }
      console.error(e);
      this.recordingOrWebcamOrMicError = true;
    }
  }

  @action.bound
  async startRecord() {
    if (this.recorder) {
      try {
        this.status = WebcamStatus.ACTIVE;
        await this.recorder.startRecording();
      } catch (e: any) {
        this.status = WebcamStatus.ERROR;
        console.error(e);
        this.recordingOrWebcamOrMicError = true;
      }
    } else {
      this.recordingOrWebcamOrMicError = true;
    }
  }

  @action.bound
  async stopRecord() {
    if (this.recorder) {
      if (this.status !== WebcamStatus.ACTIVE) {
        this.recordingOrWebcamOrMicError = true;
      }
      try {
        this.status = WebcamStatus.INACTIVE;
        await this.recorder.stopRecording();
        this.lastRecordingData = await this.recorder.getBlob();
      } catch (e: any) {
        this.status = WebcamStatus.ERROR;
        console.error(e);
        this.recordingOrWebcamOrMicError = true;
      }
    } else {
      this.recordingOrWebcamOrMicError = true;
    }
  }

  async reconnect() {
    this.recorder = null;
    this.lastRecordingData = null;
    this.status = WebcamStatus.UNINITIALIZED;
    await this.initialize();
  }

  get recordingOrWebcamOrMicError(): boolean {
    return this._recordingOrWebcamOrMicError;
  }

  set recordingOrWebcamOrMicError(value: boolean) {
    if (value) {
      this.pauseTtsAudio();
    }
    this._recordingOrWebcamOrMicError = value;
  }
}

/**
 * store initialize 시점에 value를 fix 시키기 위해 class로 wrapping
 * read 함수를 제외하고는 InterviewPracticeStore에서 currentPracticeIndex를 변경해서는 안 됨
 * 변경은 initialize 시점에 Info의 값을 copy 후 UI Store에서 observable하게 사용
 */
class PracticeQuestionInfo {
  constructor(readonly practiceQuestions: PracticeQuestionTextRs[], readonly currentPracticeIndex: number) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get questionCount() {
    return this.practiceQuestions.length;
  }
}

/*
 * TODO 적절한 코드 분할이 추후 필요합니다.
 * 현재는 ACC Project의 Webcam Service와 여기 자체의 Practice 관련 로직이 합쳐진 class입니다
 * webcam 등 Media control하는 class와 Practice control 하는 data class가 나눠져야할 듯합니다.
 * Timer도 아예 Promise 기반으로 재작성하시는걸 추천드립니다.
 */
export default class InterviewPracticeStore {
  private readonly repo: InterviewPracticeRepo;
  private readonly ttsRepo: TtsRepo;

  // 전역에서 사용하는 타이머
  @observable private timer = new AccTimer();

  @observable initialized = false;

  @observable sn: number | null = null;

  // 마이크 입력 레벨
  @observable micScale = 0;

  // 마지막으로 서버에서 불러온 동영상 파일
  @observable lastReadVideo: Blob | null = null;

  // 연습 면접 정책
  @observable policies: InterviewPracticeQuestionPolicyRs = {
    interviewPracticeMinQuestionSize: 1,
    interviewPracticeMaxQuestionSize: 10,
    minAnswerReadyTimeSec: 1,
    maxAnswerReadyTimeSec: 30,
    minAnswerTimeSec: 20,
    maxAnswerTimeSec: 90,
    maxRepeatableAnswerTimes: 2,
  };

  // 연습 질문들이 담겨져 있는 Object, 객체로 사용하는 목적은 PracticeQuestionInfo class에 있습니다.
  @observable practiceQuestionInfo = new PracticeQuestionInfo([], 0);

  // 다시하기를 누른 경험이 있는 질문 SN의 리스트, 이 리스트에 포함된 질문은 다시하기 버튼이 비활성화 됨
  @observable restartedQuestionSnList: number[] = [];

  // TTS를 재생하기 위한 오디오 객체
  @observable audio: HTMLAudioElement | null = null;

  // TODO 추후 TTS Service를 PracticeTTSStore 등으로 빼내어 Store 자체를 넘겨주는 등의 작업을 통해, 이 함수가 상단에 위치하지 않더라도 되도록 수정이 필요합니다.
  // recorderService에 적용하기 위해 상단에 선언 됨
  readonly pauseTTSAudio = () => {
    if (this.audio && !this.audio.paused) {
      this.audio.pause();
    }
  };

  readonly ttsService = new TtsService();

  readonly recorderService = new RecorderService(this.pauseTTSAudio);

  // 결과화면에서 쓸 질문 상세(의도) 데이터, 로드 되는대로 저장합니다.
  @observable questionDetails = observable.map<number, QuestionAnswerResultRs>();

  // 동영상 제출전에 Result 페이지의 데이터를 호출하는 것을 막기 위함. 추후 순차적으로 request가 진행되도록 하면 제거해도 되는 flag 변수 입니다.
  @observable allAnswersSubmitted = true;

  // 선택한 질문의 sn
  @observable selectedQuestionSn = 0;

  // 질문 데이터를 로딩중인지 체크하는 flag, loading 중에는 detail tab button을 disable 처리
  @observable questionDetailLoading = false;

  // TODO 전역 에러 핸들러를 이용해 catch 절에서 매번 true로 바꿔주지 않고도 사용할 수 있도록 개선 필요
  // Error 처리를 위한 flag, 해당 flag가 true이면 에러 화면으로 자동 이동, 그렇기 때문에 try - catch 에서 catch 부분에 이 플래그를 true로 바꿔주는 처리가 필요합니다.
  @observable private _anyError = false;

  constructor() {
    this.repo = new RemoteInterviewPracticeRepo();
    this.ttsRepo = new RemoteTtsRepo();
    makeAutoObservable(this, {}, { autoBind: true });
  }

  async initialize(sn: number) {
    try {
      this.initialized = false;

      // 아래부터 최대 3개의 Promise를 동시에 사용하기 위해 Promise.all 사용

      const promises = [this.readPracticeInfo(sn), this.readQuestionPolicy()];
      if (this.recorderService.status === WebcamStatus.UNINITIALIZED) {
        promises.push(this.recorderService.initialize());
      }
      await Promise.all(promises);
      this.initialized = true;

      // https://newbedev.com/checking-microphone-volume-in-javascript 참조
      // TODO 이 코드는 리팩토링 대상입니다. 2차 출시에서는 onAudioProcess 제외하면 좋을 것 같기도 하네요
      if (this.recorderService.recorder) {
        const audioContext = new AudioContext();
        const analyser = audioContext.createAnalyser();
        const microphone = audioContext.createMediaStreamSource(this.recorderService.recorder.stream);
        const javascriptNode = audioContext.createScriptProcessor(2048, 1, 1);

        analyser.smoothingTimeConstant = 0.8;
        analyser.fftSize = 1024;

        microphone.connect(analyser);
        analyser.connect(javascriptNode);
        javascriptNode.connect(audioContext.destination);
        javascriptNode.onaudioprocess = () => {
          const array = new Uint8Array(analyser.frequencyBinCount);
          analyser.getByteFrequencyData(array);
          this.micScale = Math.floor(array.reduce((sum, current) => sum + current, 0) / array.length);
        };
      }
      this.initialized = true;
    } catch (e: any) {
      console.error(e);
      this._anyError = true;
    }
  }

  /** API Requests */

  @request
  async readPracticeInfo(sn: number) {
    try {
      const {
        data: { practiceQuestions, runningQuestionSn },
      } = await this.repo.readInterviewPracticeQuestions(sn);

      // 서버에서 받은 현재 진행중인 질문의 SN을 기반으로, findIndex 수행
      const currentIndex = practiceQuestions.findIndex((v) => v.practiceQuestionSn === runningQuestionSn);

      // 제출한 동영상이 하나도 없으면 null이 반환되기에, currentIndex === -1 이면 0을 반환
      this.practiceQuestionInfo = new PracticeQuestionInfo(practiceQuestions, currentIndex === -1 ? 0 : currentIndex);
    } catch (e: any) {
      console.error(e);
      this._anyError = true;
    }
  }

  @request
  async saveRecord(practiceSn: number) {
    if (this.recorderService.recorder) {
      const blob = this.recorderService.lastRecordingData;
      // 저장할 데이터가 없으면 저장하지 않음
      // 하지만 저장할 데이터가 없으면 오류를 발생해야합니다. (제대로 녹화가 안된 경우입니다)
      if (blob !== null) {
        try {
          // saveRecord 중에는 lock을 걸어줍니다.
          this.allAnswersSubmitted = false;

          //웹캠 유틸을 통해 video의 duration을 가져옵니다.
          const duration = await WebcamUtil.getBlobVideoLength(blob);

          // 재생시간은 반올림
          await this.repo.savePractice(practiceSn, Math.round(duration), blob);

          // request가 끝나기에 lock 해제
          this.allAnswersSubmitted = true;
        } catch (e: any) {
          console.error(e);
          this._anyError = true;
        }
      } else {
        this._anyError = true;
      }
    }
  }

  @request
  async readQuestionIntention(questionSn: number) {
    try {
      const { data } = await this.repo.readQuestionAnswerResult(questionSn);
      if (data.helpContents === null) {
        // helpContents가 null이면 기본 값으로 채워줍니다.
        // 사실 VO로 만들어서 하는 것이 더 좋습니다.
        data.helpContents = {
          [QuestionHelpContentTypeCode.ANSWER_GUIDE]: [],
          [QuestionHelpContentTypeCode.BAD_ANSWER]: [],
          [QuestionHelpContentTypeCode.CAUTION]: [],
          [QuestionHelpContentTypeCode.GOOD_ANSWER]: [],
          [QuestionHelpContentTypeCode.QUESTION_PURPOSE]: [],
          [QuestionHelpContentTypeCode.QUESTION_PURPOSE_LINK]: [],
          [QuestionHelpContentTypeCode.SIMILAR_QUESTION_ANSWER]: [],
        };
      }
      this.questionDetails.set(questionSn, data);
    } catch (e: any) {
      console.error(e);
      this._anyError = true;
    }
  }

  @request
  async getPracticeVideo(sn: number) {
    try {
      this.lastReadVideo = (await this.repo.readPracticeVideo(sn)).data;
    } catch (e: any) {
      console.error(e);
      this._anyError = true;
    }
  }

  @request
  async readQuestionPolicy() {
    try {
      this.policies = (await this.repo.readQuestionPolicy()).data;
    } catch (e: any) {
      console.error(e);
      this._anyError = true;
    }
  }

  @request
  async playForceTTSAudio(text: string) {
    try {
      this.pauseTTSAudio();
      this.audio = await this.ttsService.getTts(text);
      await this.audio.play();
    } catch (e: any) {
      console.error(e);
      this._anyError = true;
    }
  }

  /** methods **/

  async startThinking() {
    await this.startTimer({
      maxTimeSec: this.policies.maxAnswerReadyTimeSec,
    });
  }

  stopThinking() {
    this.timer.destroy();
  }

  async startRecording() {
    await this.recorderService.startRecord();
    await this.startTimer({
      maxTimeSec: this.policies.maxAnswerTimeSec,
    });
    await this.recorderService.stopRecord();
  }

  async stopRecording() {
    await this.recorderService.stopRecord();
    this.timer.destroy();
  }

  async startTest() {
    await this.recorderService.startRecord();
    await this.startTimer({
      maxTimeSec: MAX_TEST_TIME, // TODO policy에서 가져올 수 있도록 (백엔드에서 테스트 시간을 관리할 수 있도록) 개선 필요
    });
    await this.recorderService.stopRecord();
  }

  getIsRestarted(questionSn: number) {
    return this.restartedQuestionSnList.indexOf(questionSn) > -1;
  }

  // 이름 그대로, 결과화면에서 탭 버튼은 SN이 아니라 button의 index를 가지고 있기 때문에, index기반으로 questionSn을 가져오고, detail 데이터까지 가져옵니다.
  readonly updateSelectedQuestionSnByIndex = async (index: number) => {
    try {
      this.selectedQuestionSn = this.practiceQuestionInfo.practiceQuestions[index].practiceQuestionSn;
      this.questionDetailLoading = true;
      const promises = [
        this.readQuestionIntention(this.selectedQuestionSn),
        this.getPracticeVideo(this.selectedQuestionSn),
      ];
      await Promise.all(promises);
      this.questionDetailLoading = false;
    } catch (e: any) {
      console.error(e);
      this._anyError = true;
    }
  };

  // ACC Timer가 Promise로 감싸져 있지 않아서 감쌉니다.
  private startTimer(options: Omit<ACCTimerOption, 'endCallback'>): Promise<void> {
    return new Promise<void>((solve) => {
      try {
        this.timer.initialize({
          ...options,
          endCallback: () => {
            this.timer.destroy();
            solve();
          },
        });
        this.timer.start();
      } catch (e: any) {
        console.error(e);
        this._anyError = true;
      }
    });
  }

  get mediaStream() {
    return this.recorderService.recorder?.stream ?? null;
  }

  @computed
  get recorderInitialized(): boolean {
    return this.recorderService.recorder !== null;
  }

  get recording() {
    return this.recorderService.status === WebcamStatus.ACTIVE;
  }

  get remainingTime(): number {
    return this.timer.decreasingTime;
  }

  get increasingTime(): number {
    return this.timer.increasingTime;
  }

  get lastRecordMedia() {
    return this.recorderService.lastRecordingData;
  }

  get recordingOrWebcamOrMicError() {
    return this.recorderService.recordingOrWebcamOrMicError;
  }

  // 로드된 questionDetail이 1개 이상이어야 Result 화면 랜더링 하도록
  @computed
  get resultViewRenderable() {
    return this.questionDetails.size > 0;
  }

  get anyError(): boolean {
    return this._anyError;
  }

  set anyError(value: boolean) {
    // 오류 발생시에는 TTS 강제 중지
    if (value) {
      this.pauseTTSAudio();
    }
    this._anyError = value;
  }
}
